import { Injectable, signal } from "@angular/core";

import {
  formatTechnicalInstant,
  isSupportedCompanyZone,
  parseCanonicalInstant,
} from "../utils/platform-time.util";

@Injectable({ providedIn: "root" })
export class CompanyTimeService {
  private companyZone?: string;
  private readonly _timezone = signal<string | null>(null);
  private readonly _ready = signal(false);
  readonly timezone = this._timezone.asReadonly();
  readonly ready = this._ready.asReadonly();

  beginResolution(): void {
    this.companyZone = undefined;
    this._timezone.set(null);
    this._ready.set(false);
  }

  setCompanyZone(
    timeZoneId?: string | null,
    legacyZone?: string | null,
    countryCode?: string | null,
  ): void {
    const candidate = timeZoneId || this.mapLegacyZone(legacyZone, countryCode);
    this.companyZone =
      candidate && isSupportedCompanyZone(candidate) ? candidate : undefined;
    this._timezone.set(this.companyZone ?? null);
    this._ready.set(true);
  }

  format(
    canonicalUtc?: string | Date | null,
    legacyValue?: string | null,
  ): string {
    const instant = this.parseCanonical(canonicalUtc);
    if (instant) {
      const locale = sessionStorage.getItem("lang") || "fr";
      return formatTechnicalInstant(instant, this.companyZone, locale);
    }
    return this.formatLegacyWithoutConversion(legacyValue);
  }

  parseCanonical(value?: string | Date | null): Date | null {
    return parseCanonicalInstant(value);
  }

  private formatLegacyWithoutConversion(value?: string | null): string {
    if (!value) return "—";
    const compact = value.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})/);
    if (compact) {
      return `${compact[3]}/${compact[2]}/${compact[1]} ${compact[4]}:${compact[5]}`;
    }
    const isoLike = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    return isoLike
      ? `${isoLike[3]}/${isoLike[2]}/${isoLike[1]} ${isoLike[4]}:${isoLike[5]}`
      : value;
  }

  private mapLegacyZone(
    value?: string | null,
    countryCode?: string | null,
  ): string | undefined {
    if (!value) return undefined;
    if (
      countryCode?.toUpperCase() === "MA" &&
      (value === "Morocco Standard Time" ||
        value === "Western European Summer Time" ||
        value.startsWith("UTC+"))
    ) {
      return "Africa/Casablanca";
    }
    if (/^UTC[+-]/i.test(value)) return undefined;
    return value;
  }

}
